import React, { useMemo } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

/**
 * Per-player trending block: Last 5 vs Prior 5 vs Season.
 * Metrics: PTS, REB, AST, 3P%, FTA.
 * Consumes the `game_logs` array from /api/players/{id}/details.
 */
function toNum(v) {
  if (v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

function minutesPlayed(g) {
  const m = g.min;
  if (m === null || m === undefined) return 0;
  if (typeof m === "number") return m;
  const s = String(m).trim();
  if (!s) return 0;
  if (s.includes(":")) {
    const [mm, ss] = s.split(":");
    return Number(mm || 0) + Number(ss || 0) / 60;
  }
  return Number(s) || 0;
}

function avg(rows, key) {
  if (!rows.length) return 0;
  const total = rows.reduce((s, r) => s + toNum(r[key]), 0);
  return total / rows.length;
}

// 3P% from raw makes/attempts across a set of games
function threePct(rows) {
  const m = rows.reduce((s, r) => s + toNum(r.fg3m), 0);
  const a = rows.reduce((s, r) => s + toNum(r.fg3a), 0);
  return a > 0 ? m / a : null;
}

function fmt(v, digits = 1) {
  if (v === null || v === undefined) return "—";
  return Number(v).toFixed(digits);
}

function fmtPct(v) {
  if (v === null || v === undefined) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

function DeltaBadge({ delta, isPct = false }) {
  if (delta === null || delta === undefined || Number.isNaN(delta)) {
    return <span className="text-[#757575] text-xs">—</span>;
  }
  const eps = isPct ? 0.005 : 0.05;
  const val = isPct ? `${(delta * 100).toFixed(1)}pp` : (delta >= 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1));
  if (Math.abs(delta) < eps) {
    return <span className="inline-flex items-center gap-1 text-[#a0a0a0] text-xs font-mono"><Minus className="w-3 h-3" /> flat</span>;
  }
  const up = delta > 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-mono ${up ? "text-[#4ade80]" : "text-[#f87171]"}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {up && !isPct ? val : (isPct ? (delta >= 0 ? `+${val}` : `-${(Math.abs(delta) * 100).toFixed(1)}pp`) : val)}
    </span>
  );
}

const METRICS = [
  { key: "pts", label: "Points", type: "avg", digits: 1 },
  { key: "reb", label: "Rebounds", type: "avg", digits: 1 },
  { key: "ast", label: "Assists", type: "avg", digits: 1 },
  { key: "fg3_pct", label: "3-Point %", type: "pct" },
  { key: "fta", label: "FT Attempts", type: "avg", digits: 1 },
];

export default function PlayerTrends({ gameLogs = [], window = 5, seasonAverages }) {
  const rows = useMemo(() => {
    // Consider only games actually played (min > 0). Assume game_logs sorted ascending by date.
    const played = (gameLogs || []).filter(g => minutesPlayed(g) > 0);
    const total = played.length;
    if (total < window + 1) return null;
    const last = played.slice(-window);
    const prior = total >= window * 2 ? played.slice(-2 * window, -window) : played.slice(0, Math.max(1, total - window));
    const seasonRows = played;  // whole season played

    return METRICS.map(m => {
      let lastV, priorV, seasonV;
      if (m.type === "pct") {
        lastV = threePct(last);
        priorV = threePct(prior);
        // Prefer authoritative season average if provided; otherwise compute from logs.
        seasonV = (seasonAverages && seasonAverages.fg3_pct != null) ? Number(seasonAverages.fg3_pct) : threePct(seasonRows);
      } else {
        lastV = avg(last, m.key);
        priorV = avg(prior, m.key);
        seasonV = (seasonAverages && seasonAverages[m.key] != null) ? Number(seasonAverages[m.key]) : avg(seasonRows, m.key);
      }
      const deltaPrior = (lastV !== null && priorV !== null) ? (lastV - priorV) : null;
      const deltaSeason = (lastV !== null && seasonV !== null) ? (lastV - seasonV) : null;
      return { ...m, lastV, priorV, seasonV, deltaPrior, deltaSeason };
    });
  }, [gameLogs, window, seasonAverages]);

  if (!rows) {
    return (
      <div className="border border-white/10 bg-[#1c1c1c] p-6 text-center text-[#a0a0a0] text-sm" data-testid="trends-empty">
        Not enough games logged yet to compute trends (need at least {window + 1} games played).
      </div>
    );
  }

  return (
    <div className="border border-white/10 overflow-x-auto" data-testid="player-trends">
      <table className="w-full text-sm">
        <thead className="bg-[#101010] text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">
          <tr>
            <th className="text-left px-4 py-3 border-b border-white/10 font-bold">Metric</th>
            <th className="text-right px-4 py-3 border-b border-white/10 font-bold">Last {window}</th>
            <th className="text-right px-4 py-3 border-b border-white/10 font-bold">Prior {window}</th>
            <th className="text-right px-4 py-3 border-b border-white/10 font-bold">Season</th>
            <th className="text-right px-4 py-3 border-b border-white/10 font-bold">vs Prior</th>
            <th className="text-right px-4 py-3 border-b border-white/10 font-bold">vs Season</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {rows.map((r, i) => {
            const isPct = r.type === "pct";
            const fmtV = (v) => isPct ? fmtPct(v) : fmt(v, r.digits);
            const bg = i % 2 === 0 ? "bg-[#1c1c1c]" : "bg-[#101010]";
            return (
              <tr key={r.key} className={bg} data-testid={`trend-row-${r.key}`}>
                <td className="px-4 py-3 border-b border-white/5 text-white uppercase text-[11px] tracking-[0.2em]">{r.label}</td>
                <td className="text-right px-4 py-3 border-b border-white/5 text-[var(--org-accent)] font-heading text-lg" data-testid={`trend-last-${r.key}`}>{fmtV(r.lastV)}</td>
                <td className="text-right px-4 py-3 border-b border-white/5 text-white" data-testid={`trend-prior-${r.key}`}>{fmtV(r.priorV)}</td>
                <td className="text-right px-4 py-3 border-b border-white/5 text-[#a0a0a0]" data-testid={`trend-season-${r.key}`}>{fmtV(r.seasonV)}</td>
                <td className="text-right px-4 py-3 border-b border-white/5" data-testid={`trend-vs-prior-${r.key}`}>
                  <DeltaBadge delta={r.deltaPrior} isPct={isPct} />
                </td>
                <td className="text-right px-4 py-3 border-b border-white/5" data-testid={`trend-vs-season-${r.key}`}>
                  <DeltaBadge delta={r.deltaSeason} isPct={isPct} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
